import React, { useState, useRef, useEffect, useContext } from 'react';
import { ticket, syncCircle } from 'ionicons/icons';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { IonCard, IonPopover, IonContent, IonButton, IonIcon } from '@ionic/react';
import img from '../../assets/game_trophy.svg';
import { PageContext } from '../../contexts/AppContexts';
import cbbTeams from '../../data/Sports/CBB.json';
import mlbTeams from '../../data/Sports/MLB.json';
import nbaTeams from '../../data/Sports/NBA.json';
import cfbTeams from '../../data/Sports/NCAAF.json';
import nflTeams from '../../data/Sports/NFL.json';
import nhlTeams from '../../data/Sports/NHL.json';
import nwslTeams from '../../data/Sports/NWSL.json';
import soccerTeams from '../../data/Sports/SOCCER.json';
import wnbaTeams from '../../data/Sports/WNBA.json';
import { AppDispatchContext, ACTIONS } from '../../global/AppContext';
import MatchmakingContext from '../../global/MatchmakingContext';
import { getRestaurant } from '../../global/request/restaurant';
import useTeamsUIConfig from '../../global/useTeamsUIConfig';
import '../../styles/profile/FightCard.scss';

/**
 * Card component that displays information for a single past fight.
 * @param {Object} props
 * @param {Object} props.fight The fight data to be displayed.
 * @param {string} props.fight.outcome The outcome of the fight.
 * @param {string} props.fight.msg The message for the fight.
 * @param {string} props.fight.opponent The opponent for the fight.
 * @param {string} props.fight.restaurant The restaurant for the fight.
 * @param {string} props.fight.date The date of the fight.
 * @param {string} props.fight.league The league of the fight.
 * @returns
 */

function FightCard({ fight, profileId, profileName, prizePage, bearerToken }) {
  // eslint-disable-next-line
  const { makerName, makerOutcomePhrase, takerName, takerOutcomePhrase, restaurantItems, result, timestamp } = fight;
  const { dispatch } = useContext(MatchmakingContext);
  const appDispatchCtx = useContext(AppDispatchContext);
  const pageCtx = useContext(PageContext);
  const [showPopover, setShowPopover] = useState(false);
  const [popoverEvent, setPopoverEvent] = useState(undefined);
  const [allTeams, setAllTeams] = useState([
    ...nbaTeams,
    ...nflTeams,
    ...cfbTeams,
    ...nhlTeams,
    ...mlbTeams,
    ...soccerTeams,
    ...cbbTeams,
    ...wnbaTeams,
    ...nwslTeams,
  ]);
  // eslint-disable-next-line no-unused-vars
  const [reload, specialsTeamsConfig, loading, err] = useTeamsUIConfig();
  useEffect(() => {
    setAllTeams(prevTeams => [...prevTeams, ...specialsTeamsConfig]);
  }, [specialsTeamsConfig]);

  const date = new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  });

  const determineOutcome = () => {
    if (result === profileId) {
      return {
        text: 'WIN',
        className: 'win',
        phrase: makerName === profileName ? makerOutcomePhrase : takerOutcomePhrase,
      };
    }
    if (result === 'push') {
      return {
        text: 'TIE',
        className: 'tie',
        phrase: makerName === profileName ? makerOutcomePhrase : takerOutcomePhrase,
      };
    }
    return {
      text: 'LOSS',
      className: 'loss',
      phrase: makerName === profileName ? takerOutcomePhrase : makerOutcomePhrase,
    };
  };

  const outcome = determineOutcome();

  // this really only works if the team name is 2 or 3 words, else we display full name
  const extractTeamName = (betString = '') => {
    const words = betString.trim().split(/\s+/);
    const firstTwo = words.slice(0, 2).join(' ');
    const firstThree = words.slice(0, 3).join(' ');
    return { firstTwo, firstThree };
  };

  const findTeamAcronym = (teamName1, teamName2, betstring) => {
    let foundTeam = allTeams.find(team => team.name === teamName1);
    if (foundTeam) {
      return betstring.replace(teamName1, foundTeam.acronym);
    }
    if (!foundTeam) {
      foundTeam = allTeams.find(team => team.name === teamName2);
    }
    if (foundTeam) {
      return betstring.replace(teamName2, foundTeam.acronym);
    }
    if (!foundTeam) {
      foundTeam = specialsTeamsConfig.find(team => team.name === teamName1);
    }
    if (!foundTeam) {
      foundTeam = specialsTeamsConfig.find(team => team.acronym === teamName1);
    }
    if (foundTeam) {
      return betstring.replace(teamName1, foundTeam.acronym);
    }
    return betstring;
  };

  const extractLeague = betString => {
    if (!betString) return '';
    const startIndex = betString.indexOf('(') + 1;
    return betString.substring(startIndex, startIndex + 3);
  };

  const extractBet = betString => {
    if (!betString) return '';
    const endIndex = betString.indexOf('(');
    let newBetString = '';
    if (endIndex !== -1) {
      newBetString = betString.substring(0, endIndex).trim();
    }

    const { firstTwo, firstThree } = extractTeamName(newBetString);
    const betStringAcronym = findTeamAcronym(firstTwo, firstThree, newBetString);
    return betStringAcronym;
  };

  const extractItems = itemStr => {
    return itemStr
      .split(',')
      .map(entry => {
        const trimmed = entry.trim();
        const match = trimmed.match(/(.+)\s+x(\d+)/i);

        if (!match) {
          console.warn(`Invalid format: "${entry}"`);
          return null;
        }

        const name = match[1].trim();
        const amount = parseInt(match[2], 10);

        return {
          name,
          amount,
        };
      })
      .filter(Boolean);
  };

  const handleRematchButtonClick = async () => {
    try {
      const response = await getRestaurant(bearerToken, fight.restaurantId);
      const items = await extractItems(fight.restaurantItems);

      if (!fight || !response) {
        console.error('Fight or restaurant data missing.');
        return;
      }

      const newTakername = profileName === makerName ? takerName : makerName;

      dispatch({
        type: 'SET_SELECTED_RESTAURANT',
        payload: response,
      });

      const formattedAddress = [
        response.apt,
        response.streetAddress,
        response.city && `, ${response.city}`,
        response.state && `, ${response.state}`,
        response.zip,
      ]
        .filter(Boolean)
        .join(' ')
        .replace(/\s+,/g, ',')
        .replace(/,\s+/g, ', ')
        .trim();

      appDispatchCtx.dispatch({
        action: ACTIONS.SET_RESTAURANT,
        restaurant: { ...response, address: formattedAddress },
      });

      dispatch({
        type: 'SET_CHALLENGER',
        payload: {
          name: newTakername,
        },
      });

      dispatch({
        type: 'SET_ITEMS',
        payload: items,
      });

      if (pageCtx.modals?.startFight?.current) {
        pageCtx.modals.startFight.current.present();
      }
    } catch (error) {
      console.error('[handleRematchButtonClick] Failed to fetch restaurant or process rematch:', error);
    }
  };

  const handlePopoverOpen = e => {
    e.persist();
    setPopoverEvent(e);
    setShowPopover(true);
  };

  const renderItems = () => {
    const items = restaurantItems?.split(',').map(item => item.trim());
    const itemKeys = useRef(items.map(() => uuidv4()));

    if (items?.length > 3) {
      return (
        <>
          <div role='button' tabIndex={0} onClick={handlePopoverOpen} className='items-trigger'>
            {`${items.length} items`}
          </div>

          <IonPopover isOpen={showPopover} event={popoverEvent} onDidDismiss={() => setShowPopover(false)}>
            <IonContent class='ion-padding'>
              {items.map((item, index) => (
                <div key={itemKeys.current[index]}>{item}</div>
              ))}
            </IonContent>
          </IonPopover>
        </>
      );
    }
    return (
      <div>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>{item}</div>
        ))}
      </div>
    );
  };

  const formatName = fullName => {
    const parts = fullName?.split(' ');
    if (parts?.length > 1) {
      return `${parts[0]} ${parts[1].charAt(0)}.`;
    }
    return fullName;
  };

  return (
    <>
      <div className='fight-card'>
        <div className='fight-title'>
          <p className='fight-result'>
            {profileName === makerName ? extractBet(makerOutcomePhrase) : extractBet(takerOutcomePhrase)}{' '}
          </p>
          <p className='date'>
            <span className='sport'>{extractLeague(takerOutcomePhrase)} | </span>
            {date}
          </p>
        </div>
        {outcome.text?.toLowerCase() === 'win' && (
          <img src={img} alt='Win game trophy' className='outcome-section-img' />
        )}
        <div className={`outcome-section ${outcome.text?.toLowerCase()}`}>
          <div className='outcome-text'>{outcome.text}</div>
        </div>
        <div className='fight-history-section'>
          <div className='fight-info-row'>
            <span className='fight-label'>
              <b>Vs.</b>
            </span>
            <span className='fight-value'>
              {profileName === makerName ? formatName(takerName) : formatName(makerName)}
            </span>
          </div>
          <div className='fight-info-row'>
            <span className='fight-label'>
              <b>For:</b>
            </span>
            <span className='fight-value'>{renderItems()}</span>
            {outcome.text?.toLowerCase() === 'win' ? (
              <div className='fight-card-button-wrapper'>
                <IonButton type='button' size='small' className='fight-card-button' onClick={() => prizePage()}>
                  <IonIcon icon={ticket} slot='start' />
                  View Coupon
                </IonButton>
              </div>
            ) : (
              <div className='fight-card-button-wrapper'>
                <IonButton
                  type='button'
                  size='small'
                  className='fight-card-button'
                  onClick={() => handleRematchButtonClick()}>
                  <IonIcon icon={syncCircle} slot='start' />
                  Rematch
                </IonButton>
              </div>
            )}
          </div>
        </div>
      </div>
      <IonCard button className='fight-card' style={{ display: 'none' }}>
        <div className='fight-card-l'>
          <div className={`fight-outcome ${outcome.className}`}>
            <h1>{outcome.text}</h1>
          </div>
        </div>
        <div className='fight-card-r'>
          <div className='user-info'>
            <div className='outcome-string'>{extractBet(makerOutcomePhrase)}</div>
            <div className='user-info-items'>
              <span className='bold'>{profileName === makerName ? formatName(takerName) : formatName(makerName)}</span>
              {/* <span className="bold">at Restaurant</span> */}
            </div>
            <div className='fight-items'>{renderItems()}</div>
          </div>
          <div className='right-side-content'>
            <p className='date'>{date}</p>
            <p className='sport'>{extractLeague(takerOutcomePhrase)}</p>
          </div>
        </div>
      </IonCard>
    </>
  );
}

FightCard.propTypes = {
  fight: PropTypes.shape({
    id: PropTypes.number.isRequired,
    makerBet: PropTypes.string,
    makerOutcomePhrase: PropTypes.string,
    makerName: PropTypes.string,
    makerOutcomeTeamColor: PropTypes.string,
    restaurantId: PropTypes.number,
    restaurantItems: PropTypes.string,
    result: PropTypes.string,
    // I think takerBet might have been replaced with takerOutcomePhrase and same for maker, but not certain
    takerBet: PropTypes.string,
    takerOutcomePhrase: PropTypes.string,
    takerName: PropTypes.string,
    takerOutcomeTeamColor: PropTypes.string,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
};

export default FightCard;
